import React, { Fragment, useContext, useEffect, useState, FC } from "react";
import CodeMirror from '@uiw/react-codemirror'
import { sql } from '@codemirror/lang-sql';
import { materialInit } from '@uiw/codemirror-theme-material'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { FiCheckCircle, FiChevronDown, FiChevronUp, FiEdit, FiMinus, FiPlay, FiPlus, FiRefreshCw, FiSave } from "react-icons/fi";
import { useParams } from "react-router-dom";

import DashPage from "../../dash/DashPage";

import styles from "./styles";
import flex from "../../../styles/flex";
import position from "../../../styles/position";

import TabbedView from "../../../components/lists/TabbedView/TabbedView";
import TabbedViewPage from "../../../components/lists/TabbedView/TabbedViewPage";
import { ProgressView } from "../../../components/notifications/progress/ProgressView";
import TextInputField from "../../../components/editable/EditableField/fields/TextInputField";
import ReportParameterTypeField from "../../../components/editable/EditableField/fields/ReportParameterTypeField";
import SchemaExplorer from "./components/SchemaExplorer";
import Splitter from "../../../components/splitter/Splitter";
import SwitchField from "../../../components/editable/EditableField/fields/SwitchField";
import ReportScheduler from "./components/ReportScheduler";
import ErrorDialog from "../../../components/notifications/errorDialog/ErrorDialog";
import ScheduleHistory from "./components/ScheduleHistory";

import { api, IReportGet, ReportParameter, IReportQuery, ReportParameterTypes, Report, CategoryType, Category, ReportParameterModifierTypes, ReportParameterDatePart, ReportType } from "../../../logic/api";

import { SessionContext } from "../../../utility/useSession";
import { useDate } from '../../../utility/useDate';

import NotFoundPage from "../../error/NotFoundPage";
import ReportBuilderTable from "./components/ReportBuilderTable";
import ReportParamInput from "../viewer/components/ReportParamInput";
import ReportEdit from "../../reports/components/ReportEdit";
import ReportParameterModifierField from "../../../components/editable/EditableField/fields/ReportParameterModifierField";
import NumberInputField from "../../../components/editable/EditableField/fields/NumberInputField";
import ReportParameterTimeAdjustmentField from "../../../components/editable/EditableField/fields/ReportParameterTimeAdjustmentField";
import { SimpleDropDownField } from "../../../components/editable/EditableField/fields/DropDownField";
import { enumToSelectOptions } from "../../../utility/utils";
import { ReleaseFlag, useReleaseManagement } from "../../../utility/feature-management";
import { useTranslation } from "react-i18next";


interface IReportBuilderPageParams {
    reportId: string;
}

const ReportBuilder = () => {
    const pageParams: IReportBuilderPageParams = useParams();
    const session = useContext(SessionContext);
    const { localizeDate } = useDate();
    const { t } = useTranslation();

    const reportId = isNaN(parseInt(pageParams.reportId)) ? -1 : parseInt(pageParams.reportId);
    const [fetchReportParameters] = useState<IReportGet>({
        reportId: reportId,
        values: {},
        timestamp: localizeDate(new Date()).toISO() ?? ""
    });

    const fetchReport = api.hooks.reports.getReport();

    useEffect(() => {
        fetchReport.send(session.current.context, fetchReportParameters);
    }, [fetchReportParameters]);

    const updateReportApi = api.hooks.reports.update();

    const [reportName, setReportName] = useState("");
    const [category, setCategory] = useState("");
    const [dataSource, setDataSource] = useState("mysql");
    const [query, setQuery] = useState("");
    const [headerQuery, setHeaderQuery] = useState("");
    const [footerQuery, setFooterQuery] = useState("");

    const [reportParameters, setReportParameters] = useState<ReportParameter[]>([]);

    const [newParameter, setNewParameter] = useState<ReportParameter>({
        id: -1,
        report: {} as Report,
        alias: "",
        name: "",
        defaultValue: "",
        type: ReportParameterTypes.string,
        required: true
    });

    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [selectedParameterIndex, setSelectedParameterIndex] = useState(0);

    const [params, setParams] = useState<IReportQuery | null>(null);

    const [showAddParameterDialog, setShowAddParameterDialog] = useState(false);
    const [showReportSavedDialog, setShowReportSavedDialog] = useState(false);

    const [paramValues, setParamValues] = useState<Record<string, unknown> | null>(null);
    const [showReport, setShowReport] = useState(false);
    const reportPreviewParams = { ...params, values: paramValues };
    const reportHeaderPreviewParams = headerQuery != null && headerQuery.length > 0 ? { ...{ ...params, query: headerQuery }, values: paramValues } : null;
    const reportFooterPreviewParams = footerQuery != null && footerQuery.length > 0 ? { ...{ ...params, query: footerQuery }, values: paramValues } : null;

    const isCentral = fetchReport.data?.reportType == ReportType.centralised;
    const reportTargetUuid = fetchReport.data?.targetUuid;

    useEffect(() => {
        if (fetchReport.data) {

            setReportName(fetchReport.data.name);
            setDataSource(fetchReport.data.dataSource || "mysql");
            setQuery(fetchReport.data.query == null ? "" : fetchReport.data.query);
            setHeaderQuery(fetchReport.data.headerQuery == null ? "" : fetchReport.data.headerQuery);
            setFooterQuery(fetchReport.data.footerQuery == null ? "" : fetchReport.data.footerQuery);
            setReportParameters(fetchReport.data.parameters);

            if (fetchReport.data.category != null) {
                setCategory(fetchReport.data.category.categoryName);
            }

            setParams({
                name: fetchReport.data.name,
                dataSource: fetchReport.data.dataSource,
                query: fetchReport.data.query,
                parameters: fetchReport.data.parameters,
                values: {}
            });
        }
    }, [
        fetchReport.data
    ]);

    useEffect(() => {
        setShowReport(false);
        setParams({ ...params, parameters: reportParameters } as IReportQuery);
    }, [reportParameters]);

    useEffect(() => {
        if (newParameter.type === ReportParameterTypes.datepart) {
            const defaultOptions = enumToSelectOptions(
                ReportParameterDatePart,
                { selectText: newParameter.required ? null : "None" }
            )
            const defaultValue = defaultOptions.find((option) => option.value === newParameter.defaultValue)?.value ?? defaultOptions[0].value ?? "";
            setNewParameter((param) => ({
                ...param,
                defaultValue,
            }));
        }
    }, [newParameter.type, newParameter.required])

    const runQuery = async () => {
        setParams({
            name: reportName,
            dataSource: dataSource,
            query: query,
            parameters: reportParameters,
            values: {}
        });
        setSelectedTabIndex(1);
    }

    const save = () => {
        updateReportApi.send(session.current.context, {
            id: reportId,
            name: reportName,
            parameters: reportParameters,
            dataSource: dataSource,
            query: query,
            headerQuery: headerQuery,
            footerQuery: footerQuery,
            category: category
        }, (result) => {
            if (result != null) {
                setShowReportSavedDialog(true);
            }
        });
    }

    const addParameter = () => {
        setNewParameter({
            ...newParameter,
            alias: "",
            name: "",
            defaultValue: "",
            defaultOffsetDay: 0,
            defaultOffsetMonth: 0,
            defaultOffsetYear: 0,
            defaultOffsetHour: 0,
            defaultOffsetMinute: 0,
            defaultOffsetSecond: 0,
            defaultValueFactor: 0,
            type: ReportParameterTypes.string,
            defaultValueModifier: ReportParameterModifierTypes.exact,
        })
        setShowAddParameterDialog(true);
        setSelectedParameterIndex(-1);

    }

    const confirmCreateParameter = () => {
        if (newParameter.alias.length == 0) {
            return;
        }
        if (newParameter.name.length == 0) {
            return;
        }
        setShowAddParameterDialog(false);

        if (params != null) {
            if (params.parameters[selectedParameterIndex] != null) {
                params.parameters[selectedParameterIndex] = newParameter;
                setParams(params);
                return;
            }

        }

        setSelectedParameterIndex(reportParameters.length);
        setReportParameters([...reportParameters, newParameter]);
    }

    const removeParameter = () => {
        const params = [...reportParameters];
        const param = params[selectedParameterIndex];
        params.splice(selectedParameterIndex, 1);
        setReportParameters(params);

        if (paramValues != null) {
            const newParamValues = { ...paramValues };
            delete newParamValues[param.name];
            setParamValues(newParamValues);
            setShowReport(false);
        }
    }

    const moveParameter = (amount: number) => {
        const params = [...reportParameters];
        const a = params[selectedParameterIndex];
        const b = params[selectedParameterIndex + amount];
        if (a == null || b == null) {
            return;
        }
        params[selectedParameterIndex] = b;
        params[selectedParameterIndex + amount] = a;
        setReportParameters(params);
        setSelectedParameterIndex(selectedParameterIndex + amount);
    }

    const reportTypeNames = Object.keys(ReportParameterTypes)
        .filter(a => isNaN(parseInt(a)));

    const offsetTop = 36;

    const [editReport, setEditReport] = useState<Report | null>(null);
    const [showEditReport, setShowEditReport] = useState(false);

    const material = materialInit({
        settings: {
            background: '#263238',
        },
    });

    if (fetchReport.data != null && fetchReport.data.id == null) {
        return <NotFoundPage wrapWithDash={true} title={t("errors.bookingNotFound.title")} message={t("errors.bookingNotFound.message")} />
    }

    return (
        <DashPage noScroll={true}>
            <div style={styles.wrapper}>
                {(editReport != null && editReport != null) && (
                    <ReportEdit report={editReport} show={showEditReport} onClose={(didUpdate, name, category, dataSource) => {
                        setShowEditReport(false);
                        if (didUpdate == true) {
                            setReportName(name);
                            setCategory(category);

                          if (dataSource) {
                                setDataSource(dataSource);
                            }
                        }
                    }}></ReportEdit>
                )}

                <ProgressView status={fetchReport.status}>
                    {fetchReport.data != null && (
                        <Fragment>
                            <TabbedView animated={false} selectedIndex={selectedTabIndex} onSelectedIndexChange={(indx) => setSelectedTabIndex(indx)}>
                                <TabbedViewPage visible={isCentral == false} title="MySQL">
                                    <div style={{ ...position.absoluteFill, height: "auto", top: offsetTop, borderTop: "solid 1px #333" }}>
                                        <div style={{ ...flex.flexRows, height: "100%" }}>
                                            <div style={{ ...flex.flexAuto, width: 350 }}>
                                                <Splitter>
                                                    <div style={{ borderBottom: "solid 1px #ccc" }}>
                                                        <div style={flex.flexRows}>
                                                            <div style={flex.flexAuto}>
                                                                <IconButton onClick={() => addParameter()}>
                                                                    <FiPlus color="white"></FiPlus>
                                                                </IconButton>
                                                            </div>
                                                            <div style={flex.flexAuto}>
                                                                <IconButton onClick={() => removeParameter()}>
                                                                    <FiMinus color="white"></FiMinus>
                                                                </IconButton>
                                                            </div>
                                                            <div style={flex.flexFill}>
                                                                <div style={{ paddingTop: 15, textAlign: "center", color: "white" }}>{t("reports.parameters")}</div>
                                                            </div>
                                                            <div style={flex.flexAuto}>
                                                                <IconButton onClick={() => moveParameter(-1)}>
                                                                    <FiChevronUp color="white"></FiChevronUp>
                                                                </IconButton>
                                                            </div>
                                                            <div style={flex.flexAuto}>
                                                                <IconButton onClick={() => moveParameter(1)}>
                                                                    <FiChevronDown color="white"></FiChevronDown>
                                                                </IconButton>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div style={{ overflowY: "auto", height: "100%" }}>
                                                        {reportParameters.map((param, i) => {
                                                            return <div key={i} style={{ paddingTop: 10, paddingLeft: 10, paddingRight: 10 }} onClick={() => {
                                                                setNewParameter(param);
                                                                setSelectedParameterIndex(i);
                                                                setShowAddParameterDialog(true);
                                                            }}>
                                                                <div style={{ border: "solid 1px grey", color: "white", cursor: "pointer", padding: 5, borderRadius: 5 }}>
                                                                    <div style={flex.flexRows} onClick={() => {
                                                                        setSelectedParameterIndex(i);
                                                                    }}>
                                                                        <div style={flex.flexAuto}>
                                                                            <div style={{ paddingTop: 20, paddingLeft: 10 }}>
                                                                                <input onChange={() => {
                                                                                    setSelectedParameterIndex(i);
                                                                                }} checked={selectedParameterIndex == i} tabIndex={-1} type="radio" radioGroup="params" />
                                                                            </div>
                                                                        </div>
                                                                        <div style={flex.flexFill}>
                                                                            <div style={{ paddingTop: 10, paddingLeft: 20 }}>
                                                                                {param.required == true ? (<b style={{ color: "orange" }}>({t("reports.required")})</b>) : (<b style={{ color: "green" }}>({t("reports.optional")})</b>)} <b>{param.alias}</b>
                                                                            </div>
                                                                            <div style={{ paddingBottom: 10, paddingLeft: 20 }}>
                                                                                {param.name}
                                                                            </div>
                                                                        </div>
                                                                        <div style={flex.flexAuto}>
                                                                            <div style={{ padding: 10 }}>
                                                                                {reportTypeNames[param.type]}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>;
                                                        })}
                                                    </div>

                                                </Splitter>
                                            </div>
                                            <div style={flex.flexFill}>
                                                <Splitter>
                                                    <div style={{ borderBottom: "solid 1px #ccc", borderLeft: "solid 1px #eee", borderRight: "solid 1px #eee" }}>
                                                        <div style={flex.flexRows}>
                                                            <div style={flex.flexAuto}>
                                                                <IconButton onClick={() => runQuery()}>
                                                                    <FiPlay color="white"></FiPlay>
                                                                </IconButton>
                                                            </div>
                                                            <div style={flex.flexFill}>
                                                                <div style={{ paddingTop: 15, textAlign: "center", color: "white" }}><b>{reportName.toUpperCase()}</b></div>
                                                            </div>
                                                            <div style={flex.flexAuto}>
                                                                <IconButton
                                                                    onClick={() => {
                                                                        if (fetchReport.data != null) {
                                                                            const cat: Category = {
                                                                                categoryName: category,
                                                                                id: -1,
                                                                                type: CategoryType.reports,
                                                                            };

                                                                            setEditReport({
                                                                                ...fetchReport.data,
                                                                                name: reportName,
                                                                                category: cat,
                                                                              dataSource: dataSource,
                                                                            });
                                                                            setShowEditReport(true);
                                                                        }

                                                                    }}>
                                                                    <FiEdit color="white"></FiEdit>
                                                                </IconButton>
                                                            </div>
                                                            <div style={flex.flexAuto}>
                                                                <IconButton title="Save" onClick={() => save()}>
                                                                    <FiSave color="white"></FiSave>
                                                                </IconButton>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div style={{ borderLeft: "solid 1px #eee", borderRight: "solid 1px #eee", height: "100%", overflow: "auto" }}>
                                                        <TabbedView preLoad={true} animated={false}>
                                                            <TabbedViewPage title="Query">
                                                                <CodeMirror
                                                                    value={query}
                                                                    onChange={(value) => setQuery(value)}
                                                                    theme={material}
                                                                    extensions={[sql()]}
                                                                    basicSetup={{
                                                                        foldGutter: false,
                                                                        lineNumbers: false,
                                                                    }}
                                                                />
                                                            </TabbedViewPage>

                                                            <TabbedViewPage title={t("reports.headerQueryPDF")}>
                                                                <CodeMirror
                                                                    value={headerQuery}
                                                                    onChange={(value) => setHeaderQuery(value)}
                                                                    theme={material}
                                                                    extensions={[sql()]}
                                                                    basicSetup={{
                                                                        foldGutter: false,
                                                                        lineNumbers: false,
                                                                    }}
                                                                />
                                                            </TabbedViewPage>

                                                            <TabbedViewPage title={t("reports.footerQueryPDF")}>
                                                                <CodeMirror
                                                                    value={footerQuery}
                                                                    onChange={(value) => setFooterQuery(value)}
                                                                    theme={material}
                                                                    extensions={[sql()]}
                                                                    basicSetup={{
                                                                        foldGutter: false,
                                                                        lineNumbers: false,
                                                                    }}
                                                                />
                                                            </TabbedViewPage>
                                                        </TabbedView>
                                                    </div>
                                                </Splitter>
                                            </div>
                                            <div style={{ ...flex.flexAuto, width: 350 }}>

                                                <Splitter>
                                                    <div style={{ borderBottom: "solid 1px #ccc" }}>
                                                        <div style={flex.flexRows}>

                                                            <div style={flex.flexFill}>
                                                                <div style={{ paddingTop: 15, textAlign: "center", color: "white" }}>{t("reports.schema")}</div>
                                                            </div>
                                                            <div style={flex.flexAuto}>
                                                                <div style={{ visibility: "hidden" }}> <IconButton>
                                                                    <FiRefreshCw color="white"></FiRefreshCw>
                                                                </IconButton></div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div style={{ overflow: "auto", height: "100%" }}>
                                                        <SchemaExplorer dataSource={dataSource} />
                                                    </div>
                                                </Splitter>
                                            </div>
                                        </div>
                                    </div>
                                </TabbedViewPage>
                                <TabbedViewPage visible={isCentral == false} title="Preview">
                                    <div style={{ ...position.absoluteFill, height: "auto", top: offsetTop, overflow: "auto" }}>

                                        <div style={{ padding: 5 }}>
                                            {params != null && (<ReportParamInput
                                                report={params}
                                                values={paramValues}
                                                onParamsChange={(values, show) => {
                                                    setParamValues(values);
                                                    setShowReport(show);
                                                }}
                                            ></ReportParamInput>)}

                                            {(showReport == true && reportHeaderPreviewParams != null) && (
                                                <div>
                                                    <div><b style={{ color: "white" }}>{t("reports.header")}</b></div>
                                                    <ReportBuilderTable report={reportHeaderPreviewParams as IReportQuery}></ReportBuilderTable>
                                                </div>
                                            )}
                                            {(showReport == true && reportFooterPreviewParams != null) && (
                                                <div>
                                                    <div><b style={{ color: "white" }}>{t("reports.footer")}</b></div>
                                                    <ReportBuilderTable report={reportFooterPreviewParams as IReportQuery}></ReportBuilderTable>
                                                </div>
                                            )}
                                            {showReport == true && (
                                                <div>
                                                    <div><b style={{ color: "white" }}>{t("reports.content")}</b></div>
                                                    <ReportBuilderTable report={reportPreviewParams as IReportQuery}></ReportBuilderTable>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </TabbedViewPage>
                                <TabbedViewPage title={t("reports.schedule")}>
                                    <ReportScheduler reportId={reportId} reportTargetUuid={reportTargetUuid}></ReportScheduler>
                                </TabbedViewPage>
                                <TabbedViewPage title={t("reports.scheduleHistory")}>
                                    <ScheduleHistory reportId={reportId} reportTargetUuid={reportTargetUuid}></ScheduleHistory>
                                </TabbedViewPage>
                            </TabbedView>


                            <Dialog
                                open={showReportSavedDialog}
                                keepMounted

                                aria-labelledby="alert-dialog-slide-title"
                                aria-describedby="alert-dialog-slide-description"
                            >
                                <DialogTitle id="alert-dialog-slide-title">{t("reports.reportSaved")}</DialogTitle>
                                <DialogContent>
                                    <div style={{ textAlign: "center" }}>
                                        <FiCheckCircle size={40} color="green"></FiCheckCircle>
                                    </div>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setShowReportSavedDialog(false)} color="primary">{t("general.ok")}</Button>
                                </DialogActions>
                            </Dialog>
                            <Dialog
                                open={showAddParameterDialog}
                                keepMounted
                                aria-labelledby="alert-dialog-slide-title"
                                aria-describedby="alert-dialog-slide-description"
                            >
                                <DialogTitle id="alert-dialog-slide-title">{t("reports.reportParameter")}</DialogTitle>
                                <DialogContent>
                                    <div style={{ paddingTop: 10 }}>
                                        <TextInputField label={t("reports.alias")} value={newParameter.alias} onChange={(value) => {
                                            setNewParameter({ ...newParameter, alias: value });
                                        }} />
                                    </div>
                                    <div style={{ paddingTop: 10 }}>
                                        <TextInputField label={t("input.labels.name")} value={newParameter.name} onChange={(value) => {
                                            value = value.replace(/ /g, "");
                                            if (!value.startsWith("@u_")) {
                                                value = `@u_${value}`;
                                            }
                                            setNewParameter({ ...newParameter, name: value });
                                        }} />
                                    </div>


                                    <div style={{ paddingTop: 10 }}>
                                        <div style={{ marginBottom: 5 }}>{t("reports.fieldType")}:</div>
                                        <ReportParameterTypeField label={"Name"} value={newParameter.type} onChange={(value) => {
                                            newParameter.defaultValueModifier = ReportParameterModifierTypes.exact;
                                            setNewParameter({ ...newParameter, type: value, defaultValueModifier: ReportParameterModifierTypes.exact });
                                        }}></ReportParameterTypeField>
                                    </div>

                                    {
                                        ([ReportParameterTypes.date, ReportParameterTypes.datetime,].indexOf(newParameter.type) != -1) && (
                                            <div style={{ paddingTop: 10 }}>
                                                <div style={{ marginBottom: 5 }}>{t("reports.defaultValueType")}:</div>
                                                <ReportParameterModifierField label={"Value Type"} value={newParameter.defaultValueModifier} onChange={(value) => {
                                                    setNewParameter({ ...newParameter, defaultValueModifier: value });
                                                }}></ReportParameterModifierField>
                                            </div>
                                        )
                                    }


                                    {
                                        (([ReportParameterTypes.date, ReportParameterTypes.datetime,].indexOf(newParameter.type) != -1) && newParameter.defaultValueModifier != ReportParameterModifierTypes.dateOffset) && (
                                            <div style={{ paddingTop: 10 }}>
                                                <div style={{ marginBottom: 5 }}>{t("reports.timeAdjustment")}:</div>
                                                <ReportParameterTimeAdjustmentField label={"Time"} value={newParameter.defaultTimeType} onChange={(value) => {
                                                    setNewParameter({ ...newParameter, defaultTimeType: value });
                                                }}></ReportParameterTimeAdjustmentField>
                                            </div>
                                        )
                                    }


                                    {
                                        (newParameter.defaultValueModifier == ReportParameterModifierTypes.dateOffset && [ReportParameterTypes.date, ReportParameterTypes.datetime,].indexOf(newParameter.type) != -1) && (
                                            <div style={{ paddingTop: 50 }}>

                                                <NumberInputField label={t("reports.addDays")} value={newParameter.defaultOffsetDay} onChange={(value) => {
                                                    setNewParameter({ ...newParameter, defaultOffsetDay: value });
                                                }} />
                                            </div>
                                        )
                                    }
                                    {
                                        (newParameter.defaultValueModifier == ReportParameterModifierTypes.dateOffset && [ReportParameterTypes.date, ReportParameterTypes.datetime,].indexOf(newParameter.type) != -1) && (
                                            <div style={{ paddingTop: 10 }}>

                                                <NumberInputField label={t("reports.addMonths")} value={newParameter.defaultOffsetMonth} onChange={(value) => {
                                                    setNewParameter({ ...newParameter, defaultOffsetMonth: value });
                                                }} />
                                            </div>
                                        )
                                    }
                                    {
                                        (newParameter.defaultValueModifier == ReportParameterModifierTypes.dateOffset && [ReportParameterTypes.date, ReportParameterTypes.datetime,].indexOf(newParameter.type) != -1) && (
                                            <div style={{ paddingTop: 10 }}>

                                                <NumberInputField label={t("reports.addYears")} value={newParameter.defaultOffsetYear} onChange={(value) => {
                                                    setNewParameter({ ...newParameter, defaultOffsetYear: value });
                                                }} />
                                            </div>
                                        )
                                    }
                                    {
                                        (newParameter.defaultValueModifier == ReportParameterModifierTypes.dateOffset && [ReportParameterTypes.date, ReportParameterTypes.datetime,].indexOf(newParameter.type) != -1) && (
                                            <div style={{ paddingTop: 50 }}>

                                                <NumberInputField label={t("reports.addHours")} value={newParameter.defaultOffsetHour} onChange={(value) => {
                                                    setNewParameter({ ...newParameter, defaultOffsetHour: value });
                                                }} />
                                            </div>
                                        )
                                    }
                                    {
                                        (newParameter.defaultValueModifier == ReportParameterModifierTypes.dateOffset && [ReportParameterTypes.date, ReportParameterTypes.datetime,].indexOf(newParameter.type) != -1) && (
                                            <div style={{ paddingTop: 10 }}>

                                                <NumberInputField label={t("reports.addMins")} value={newParameter.defaultOffsetMinute} onChange={(value) => {
                                                    setNewParameter({ ...newParameter, defaultOffsetMinute: value });
                                                }} />
                                            </div>
                                        )
                                    }
                                    {
                                        (newParameter.defaultValueModifier == ReportParameterModifierTypes.dateOffset && [ReportParameterTypes.date, ReportParameterTypes.datetime,].indexOf(newParameter.type) != -1) && (
                                            <div style={{ paddingTop: 10 }}>

                                                <NumberInputField label={t("reports.addSecs")} value={newParameter.defaultOffsetSecond} onChange={(value) => {

                                                    setNewParameter({ ...newParameter, defaultOffsetSecond: value });
                                                }} />
                                            </div>
                                        )
                                    }

                                    {
                                        (newParameter.defaultValueModifier == ReportParameterModifierTypes.exact && [ReportParameterTypes.string].indexOf(newParameter.type) != -1) && (
                                            <div style={{ paddingTop: 10 }}>

                                                <TextInputField label={t("reports.defaultValue")} value={newParameter.defaultValue} onChange={(value) => {
                                                    setNewParameter({ ...newParameter, defaultValue: value });
                                                }} />
                                                <div style={{ paddingTop: 50 }}></div>
                                            </div>
                                        )
                                    }
                                    {
                                        (newParameter.defaultValueModifier == ReportParameterModifierTypes.exact && [ReportParameterTypes.number,].indexOf(newParameter.type) != -1) && (
                                            <div style={{ paddingTop: 10 }}>

                                                <NumberInputField label={t("reports.defaultValue")} value={parseInt(newParameter.defaultValue)} onChange={(value) => {
                                                    let v = parseInt(`${value}`);
                                                    if (isNaN(v)) {
                                                        v = 0;
                                                    }
                                                    setNewParameter({ ...newParameter, defaultValue: `${v}` });
                                                }} />
                                                <div style={{ paddingTop: 50 }}></div>
                                            </div>
                                        )
                                    }
                                    {
                                        ([ReportParameterTypes.datepart].includes(newParameter.type)) && (
                                            <DatePartOptions newParameter={newParameter} setNewParameter={setNewParameter}></DatePartOptions>
                                        )
                                    }

                                    <div style={{ paddingTop: 50 }}>
                                        <div>{t("reports.reqField")}:</div>
                                        <SwitchField value={newParameter.required} onChange={(value) => {
                                            setNewParameter({ ...newParameter, required: value == true });
                                        }}></SwitchField>
                                    </div>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setShowAddParameterDialog(false)} color="secondary">{t("general.cancel")}</Button>
                                    <Button onClick={() => confirmCreateParameter()} color="primary">{t("general.ok")}</Button>
                                </DialogActions>
                            </Dialog>



                        </Fragment>


                    )}
                </ProgressView>
                <ErrorDialog err={updateReportApi.err}></ErrorDialog>
            </div>
        </DashPage >
    );
}

interface IDatePartOptionsProps {
    newParameter: ReportParameter;
    setNewParameter: (value: ReportParameter) => void;
}

const DatePartOptions: FC<IDatePartOptionsProps> = ({
    newParameter,
    setNewParameter,
}) => {
    const { t } = useTranslation();
    return <div style={{ paddingTop: 10 }}>
        <div style={{ marginBottom: 5 }}>{t("reports.defaultValue")}:</div>

        <SimpleDropDownField
            label={""}
            value={newParameter.defaultValue}
            items={enumToSelectOptions(
                ReportParameterDatePart,
                { selectText: newParameter.required ? null : t("general.none") }
            )}
            onChange={(value) => {
                setNewParameter({
                    ...newParameter,
                    defaultValue: value.value || "",
                });
            }}
        >
        </SimpleDropDownField>
    </div>
}

export default ReportBuilder;
